import { useMemo } from "react";

import { useSessionStore } from "@/core/stores/session.store";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { useAgentByKeycloak } from "@/features/kyc/api/use-agent-by-keycloak";
import {
  AgentQrCard,
  NonMemberRedeemForm,
} from "@/features/wallet/components";

/** Décode le payload JWT (base64url) sans vérification de signature. */
function decodeJwtSub(token: string | null): string | null {
  if (!token) return null;
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    const json = atob(padded);
    const claims = JSON.parse(json) as Record<string, unknown>;
    return typeof claims.sub === "string" ? claims.sub : null;
  } catch {
    return null;
  }
}

/**
 * Page agent (guide v2.4 §6.3) — bloc QR agent statique à présenter aux
 * clients pour dépôt espèces, et formulaire d'encaissement Non-Member
 * (verify + redeem idempotent, commission agent provisionnée).
 * Garde de rôle `flashpay-agents` posée côté router.
 */
export function AgentMePage() {
  const { t } = useTypedTranslation();
  const tx = (key: string) => t(key as never, {} as never) as string;
  const accessToken = useSessionStore((state) => state.accessToken);
  const keycloakUserId = useMemo(() => decodeJwtSub(accessToken), [accessToken]);
  const { data: agent, isLoading } = useAgentByKeycloak(
    keycloakUserId ?? undefined
  );

  return (
    <section className="flex flex-col gap-4 max-w-3xl mx-auto">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-foreground">
          {tx("agents.me.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {tx("agents.me.subtitle")}
        </p>
      </div>

      <AgentQrCard />

      {agent?.id ? (
        // L'AgentDto ne porte pas `partyId` ; on retombe sur `keycloakUserId`
        // en attendant une résolution serveur (sinon le back refuse 422).
        <NonMemberRedeemForm
          agentId={agent.id}
          agentPartyId={agent.keycloakUserId}
        />
      ) : isLoading ? null : (
        <p className="text-xs text-muted-foreground">
          {tx("agents.me.noAgentProfile")}
        </p>
      )}
    </section>
  );
}
